import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  BarElement,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { RootState } from '@/redux';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

// Register Chart.js components
ChartJS.register(LineElement, PointElement, BarElement, LinearScale, CategoryScale, Title, Tooltip, Legend, Filler);

export const MRRCharts = () => {
  const { mrr } = useSelector((state: RootState) => state.adminDashboard);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [lineChartData, setLineChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [barChartData, setBarChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  // Build cumulative MRR over time
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildLineChartData = (subs: any[]) => {
    const validSubs = subs.filter(sub => sub && sub.currentPeriodEnd);
    if (!validSubs.length) return { labels: [], datasets: [] };

    // Sort subscriptions by currentPeriodEnd ascending
    const sortedSubs = [...validSubs].sort(
      (a, b) => dayjs(a.currentPeriodEnd).unix() - dayjs(b.currentPeriodEnd).unix()
    );

    const labels: string[] = [];
    const data: number[] = [];
    let cumulative = 0;

    sortedSubs.forEach(s => {
      const dateLabel = dayjs(s.currentPeriodEnd).format('YYYY-MM-DD');
      cumulative += s.amountCents / 100;

      // Avoid duplicate labels
      if (!labels.includes(dateLabel)) {
        labels.push(dateLabel);
        data.push(cumulative);
      } else {
        data[data.length - 1] = cumulative;
      }
    });

    return {
      labels,
      datasets: [
        {
          label: 'Cumulative MRR ($)',
          data,
          fill: true,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.4,
          pointStyle: 'rectRot',
          pointRadius: 6,
        },
      ],
    };
  };

  useEffect(() => {
    if (!mrr) return;

    const activeSubs = mrr.subscriptions.filter(s => s.status === 'active');

    // TODO: In future, we can filter by date ranges when we have enough historical data
    setLineChartData(buildLineChartData(activeSubs));

    // Build bar chart (Top 10 active subscriptions)
    const top10 = [...activeSubs]
      .sort((a, b) => b.amountCents - a.amountCents)
      .slice(0, 10);

    setBarChartData({
      labels: top10.map(s => s.organizationName ?? 'Unlinked'),
      datasets: [
        {
          data: top10.map(s => s.amountCents / 100),
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)',
            'rgba(255, 159, 64, 0.2)',
            'rgba(255, 205, 86, 0.2)',
            'rgba(75, 192, 192, 0.2)',
            'rgba(54, 162, 235, 0.2)',
            'rgba(153, 102, 255, 0.2)',
            'rgba(201, 203, 207, 0.2)',
          ],
          borderColor: [
            'rgb(255, 99, 132)',
            'rgb(255, 159, 64)',
            'rgb(255, 205, 86)',
            'rgb(75, 192, 192)',
            'rgb(54, 162, 235)',
            'rgb(153, 102, 255)',
            'rgb(201, 203, 207)',
          ],
          borderWidth: 1,
        },
      ],
    });
  }, [mrr]);

  const lineOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      legend: false as any,
      title: { display: true, text: 'Cumulative Monthly Recurring Revenue' },
      tooltip: { mode: 'index', intersect: false },
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
    scales: {
      x: {
        display: true,
        title: { display: true, text: 'Date' },
        grid: { display: true, color: 'rgba(200,200,200,0.2)' },
      },
      y: {
        display: true,
        title: { display: true, text: 'MRR ($)' },
        grid: { display: true, color: 'rgba(200,200,200,0.2)' },
      },
    },
  };

  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      legend: false as any,
      title: { display: true, text: 'Top 10 Active Organizations' },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            return `$${value?.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (_, index) {
            const label = barChartData.labels[index];
            return label.length > 5 ? `${label.substring(0, 5)}...` : label;
          },
        },
        grid: { display: false },
      },
      y: { beginAtZero: true },
    },
  };

  return (
    <div className='w-full flex flex-col'>
      <div className='flex'>
        <div className='w-1/2 bg-foreground rounded-md p-2'>
          <Line data={lineChartData} options={lineOptions} />
        </div>
        <div className='mx-3'></div>
        <div className='w-1/2 bg-foreground rounded-md p-2'>
          <Bar data={barChartData} options={barOptions} />
        </div>
      </div>
    </div>
  );
};
